import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { listingClient } from '../../../../clients'
import partnerClient from '../../../../clients/partner-client'
import { RequestUserConfig } from '../../../../common/types'
import { encodeToBase64 } from '../../../../utils/encoding-utils'
import {
    PAGINATION,
    DataSource,
    SectionType,
    SEARCH_REQUEST_ID_HEADER,
} from '../../../../constants/search-result-listing'
import ListingSectionWithMetadata from '../../../../transports/listing-section-with-metadata-transport'
import { ListingSectionObject } from '../../../../transports/common/listing-section-transport'
import { createSectionFromTransport, createPartnerSection, compactSections } from '../../../../utils/section-adapters'
import { useTranslation } from '../../../../hooks/locale'
import { useABConfig } from '../../../../context/ab-config-context'
import { ABConfigResponseTransport } from '../../../../transports/ab-config-transport'
import { SRLNoResultTransport } from '../../../../transports/srl-objects-response-transport'
import { getSearchRequestId, setSearchRequestId } from '../../../../redux/filters'
import { TraumTagTypes } from '../../../../utils/traum-tag-utils'
import { TraumTagObjectsRequestTransport } from '../../../../transports/traum-tag-objects-transport'
import { useAppDispatch } from '../../../../redux/hooks'

const { ITEMS_PER_PAGE } = PAGINATION

interface FetchSectionPageResult {
    sections: ListingSectionWithMetadata[]
    totalItems: number
    primaryHeading?: string
    srlNoResult?: SRLNoResultTransport
    searchRequestId?: string
}

/**
 * Fetches main SRL listings and converts to sections
 */
export const fetchSRLListings = async (
    user: RequestUserConfig,
    filter: string,
    page: number,
    abConfig?: ABConfigResponseTransport,
    searchRequestId?: string,
): Promise<FetchSectionPageResult> => {
    const encodedFilter = encodeToBase64(filter)
    const offset = (page - 1) * ITEMS_PER_PAGE

    const response = await listingClient.getSearchResultListingObjects({
        user,
        params: {
            filter: encodedFilter,
            page,
            pageSize: ITEMS_PER_PAGE,
            offset,
        },
        body: abConfig,
        searchRequestId,
    })

    if (!response.data?.data) {
        return { sections: [], totalItems: 0 }
    }

    const { primarySection, severedFillUpSection, fillUpSection, srlNoResult } = response.data.data
    const responseSearchRequestId = response.headers?.[SEARCH_REQUEST_ID_HEADER] as string | undefined

    const sections = compactSections([
        createSectionFromTransport(primarySection, page, SectionType.PRIMARY, DataSource.MAIN),
        createSectionFromTransport(severedFillUpSection, page, SectionType.SEVERED_FILLUP, DataSource.MAIN),
        createSectionFromTransport(fillUpSection, page, SectionType.FILLUP, DataSource.MAIN),
    ])

    const totalItems = primarySection?.totalCount ?? 0
    const primaryHeading = primarySection?.heading ?? ''

    return { sections, totalItems, primaryHeading, srlNoResult, searchRequestId: responseSearchRequestId }
}

/**
 * Hook that returns a memoized fetch function for use with useLazySectionedPagination
 */
export const useFetchSRLListings = (user: RequestUserConfig, filter: string) => {
    const { abConfigData } = useABConfig()
    const dispatch = useAppDispatch()
    const searchRequestId = useSelector(getSearchRequestId)
    return useCallback(
        async (page: number) => {
            const result = await fetchSRLListings(user, filter, page, abConfigData ?? undefined, searchRequestId)

            if (result.searchRequestId) {
                dispatch(setSearchRequestId(result.searchRequestId))
            }
            return result
        },
        [user, filter, abConfigData, searchRequestId, dispatch],
    )
}

/**
 * Fetches Holidu partner listings
 */
const fetchHoliduListings = async (
    user: RequestUserConfig,
    filter: string,
    page: number,
    t: ReturnType<typeof useTranslation>['t'],
): Promise<FetchSectionPageResult> => {
    const encodedFilter = encodeToBase64(filter)

    const response = await partnerClient.searchHolidu({
        user,
        params: {
            srlUri: encodedFilter,
            pageNumber: page,
            pageSize: ITEMS_PER_PAGE,
        },
    })

    const objects = response.data?.objects || []

    if (objects.length === 0) {
        return { sections: [], totalItems: 0 }
    }

    const section = createPartnerSection(objects, page, DataSource.HOLIDU, t, response.data?.totalCount)

    const sections = section ? [section] : []
    const totalItems = response.data?.totalCount ?? sections[0]?.objects.length ?? 0

    return { sections, totalItems }
}

/**
 * Hook wrapper for Holidu fetch
 */
export const useFetchHoliduListings = (user: RequestUserConfig, filter: string) => {
    const { t } = useTranslation()
    return useCallback((page: number) => fetchHoliduListings(user, filter, page, t), [user, filter, t])
}

export const useHiddenGemListings = (
    user: RequestUserConfig,
    filter: string,
    excludedObjectKeys: string[],
    isInitializing: boolean,
    isActive: boolean,
) => {
    const [listings, setListings] = useState<ListingSectionObject[]>([])
    const [loading, setLoading] = useState(false)
    const encodedFilter = encodeToBase64(filter)
    const excludedObjectKeysRef = useRef(excludedObjectKeys)
    excludedObjectKeysRef.current = excludedObjectKeys

    useEffect(() => {
        if (!isActive || isInitializing || excludedObjectKeysRef.current.length === 0) {
            return
        }
        setLoading(true)
        const body: TraumTagObjectsRequestTransport = {
            traumTagIds: [TraumTagTypes.HIDDEN_GEM],
            excludedObjectKeys: excludedObjectKeysRef.current,
        }

        listingClient
            .getTraumTagObjects({ user, filter: encodedFilter, body })
            .then(res => setListings(res?.data?.objects ?? []))
            .catch(() => setListings([]))
            .finally(() => setLoading(false))
    }, [encodedFilter, isActive, isInitializing, user])

    return { listings, loading }
}
